<script setup>
// The club-clock name, shown only to a reader on another clock (TIME_ZONE_PLAN D2): as a quiet note under a screen or
// card title ("Times are in UK time", a 14px clock icon), or inline after one headline time ("19:00 UK time"). Nothing
// at home, nothing on the server, nothing when the club's zone is unknown. Hover or tap the label for your own time (D7).
import {computed} from 'vue';
import {useClubClock} from '~/composables/useClubClock';

const props = defineProps({
  /** The club's zone (effectiveTimeZone ?? timeZone, a fixture's clubTimeZone, a bookings response's timeZone). */
  zone: {type: String, default: null},
  /** The API's own name for it, when the response carries one (timeZoneLabel); else the shared table names it. */
  label: {type: String, default: null},
  /** The instants on the screen (or the one headline time): away is judged on them, not on today. */
  instants: {type: Array, default: () => []},
  /** The one headline time: the label sits inline after it. */
  inline: {type: Boolean, default: false},
});
const {awayLabelFor, noteFor, yourTime} = useClubClock();
const first = computed(() => (props.instants ?? []).find(Boolean) ?? null);
const text = computed(() => (props.inline
    ? awayLabelFor(first.value, props.zone, props.label)
    : noteFor(props.zone, props.instants ?? [], props.label)));
const hover = computed(() => (props.inline && first.value ? yourTime(first.value) : null));
</script>

<template>
  <template v-if="text">
    <v-tooltip v-if="hover" :text="hover" location="top">
      <template #activator="{props: tip}">
        <span v-bind="tip" class="tl-clock-inline" data-clock-label>{{ text }}</span>
      </template>
    </v-tooltip>
    <span v-else-if="inline" class="tl-clock-inline" data-clock-label>{{ text }}</span>
    <div v-else class="tl-clock-note" data-clock-note role="note">
      <v-icon size="14" icon="mdi-clock-outline"/>
      <span>{{ text }}</span>
    </div>
  </template>
</template>

<style scoped>
.tl-clock-note {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 8px;
}

.tl-clock-inline {
  font-weight: 400;
  color: rgb(var(--v-theme-textSubtle));
  white-space: nowrap;
}
</style>
